import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { donedealBreakpoints } from 'styled-system';
import {
  ProviderDisplayName,
  QueryParameters,
} from './queries/schema/carFinance.schema';
import { setStartTime } from '../../helpers/browserStorageTimer';
import { pushToDataLayer } from '../../services/gtmService';
import { ApplicationComplete } from './components/ApplicationComplete/ApplicationComplete';
import { LoadError } from './components/LoadError/LoadError';
import { Introduction } from './components/Panels/Introduction/Introduction';
import { SwitchOnProvider } from './components/SwitchOnProvider/SwitchOnProvider';
import { FINANCE_APPLICATION_START_TIME } from './constants';
import { onSubmitTrackingEvent } from './financeProviders/shared/tracking/tracking';
import { RESULT } from './financeProviders/shared/tracking/tracking.typed';
import { useGetCarFinanceInitialFormData } from './queries/hooks/useGetCarFinanceInitialFormData';
import { Loading } from './components/Loading/Loading';

const Wrapper = styled.div`
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  min-height: 720px;

  ${donedealBreakpoints.media.medium} {
    padding: ${({ theme }) => `${theme.spacing.M24} 0 ${theme.spacing.L56}`};
  }
`;

const Panel = styled.div`
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => `${theme.spacing.M24} ${theme.spacing.M16}`};

  ${donedealBreakpoints.media.medium} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

type CarFinancePageProps = { query: QueryParameters };

const CarFinancePage = ({ query }: CarFinancePageProps) => {
  const { adId, make, model, price, provider } = query;

  const {
    isPending: initialFormDataPending,
    data: initialFormData,
    isError: initialFormDataError,
  } = useGetCarFinanceInitialFormData(query);

  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const updateToSuccessState = () => {
    onSubmitTrackingEvent({ result: RESULT.SUCCESS, provider });
    setIsSubmissionSuccessful(true);
  };

  const onSubmitErrorCallback = (errorFields: Array<string>) => {
    onSubmitTrackingEvent({
      result: RESULT.FAILURE,
      provider,
      errorFields,
    });
  };

  useEffect(() => {
    if (
      !localStorage.getItem(FINANCE_APPLICATION_START_TIME) &&
      !sessionStorage.getItem(FINANCE_APPLICATION_START_TIME)
    ) {
      setStartTime(
        FINANCE_APPLICATION_START_TIME,
        new Date().getTime().toString(),
      );
    }
  }, []);

  useEffect(() => {
    pushToDataLayer({
      event: 'car_finance_form_loaded',
      financeProvider: ProviderDisplayName[provider],
    });
  }, [provider]);

  return (
    <Wrapper>
      <Panel as="div">
        {!isSubmissionSuccessful && (
          <>
            {!initialFormDataPending && initialFormData && (
              <>
                <Introduction
                  provider={provider}
                  // only show logo and heading on first step
                  isStepOne={currentStep === 1}
                />
                <SwitchOnProvider
                  query={query}
                  onSubmitCallback={updateToSuccessState}
                  onSubmitErrorCallback={onSubmitErrorCallback}
                  onStepChange={setCurrentStep}
                  initialFormData={initialFormData}
                  adId={adId}
                  make={make}
                  model={model}
                  provider={provider}
                  price={price}
                />
              </>
            )}
            {!initialFormDataPending && initialFormDataError && <LoadError />}
            {initialFormDataPending && <Loading />}
          </>
        )}
        {isSubmissionSuccessful && <ApplicationComplete />}
      </Panel>
    </Wrapper>
  );
};

export { CarFinancePage };
